/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import { colors } from '../../shared/theme/colors';

/** EMVCo MP es denso: quiet zone blanca solo en el recuadro inmediato al QR. */
const QR_ERROR_CORRECTION: 'L' | 'M' | 'Q' | 'H' = 'L';
const QR_QUIET_ZONE_MODULES = 8;
const QR_WHITE_PAD = 32;
const QR_MAX_DISPLAY = 400;
const QR_MIN_DISPLAY = 300;
const QR_WEB_RENDER_SCALE = 5;

type MercadoPagoQrCodeProps = {
  value: string;
};

export function MercadoPagoQrCode({ value }: MercadoPagoQrCodeProps) {
  const { width: windowWidth } = useWindowDimensions();
  const [webDataUrl, setWebDataUrl] = useState<string | null>(null);
  const [webGenerating, setWebGenerating] = useState(Platform.OS === 'web');

  const displaySize = useMemo(() => {
    const horizontalBudget = Math.max(QR_MIN_DISPLAY, windowWidth - 120);
    return Math.min(QR_MAX_DISPLAY, horizontalBudget);
  }, [windowWidth]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !value.trim()) {
      setWebDataUrl(null);
      setWebGenerating(false);
      return;
    }
    let alive = true;
    setWebGenerating(true);
    setWebDataUrl(null);
    void import('qrcode')
      .then((lib) =>
        lib.default.toDataURL(value.trim(), {
          width: displaySize * QR_WEB_RENDER_SCALE,
          margin: QR_QUIET_ZONE_MODULES,
          errorCorrectionLevel: QR_ERROR_CORRECTION,
          color: { dark: '#000000', light: '#FFFFFF' },
        })
      )
      .then((url) => {
        if (!alive) return;
        setWebDataUrl(url);
        setWebGenerating(false);
      })
      .catch(() => {
        if (!alive) return;
        setWebDataUrl(null);
        setWebGenerating(false);
      });
    return () => {
      alive = false;
    };
  }, [displaySize, value]);

  const whitePlateStyle = useMemo(
    () => [
      styles.whitePlate,
      {
        width: displaySize + QR_WHITE_PAD * 2,
        minHeight: displaySize + QR_WHITE_PAD * 2,
      },
    ],
    [displaySize]
  );

  return (
    <View style={styles.host}>
      <View style={whitePlateStyle} accessibilityRole="image" accessibilityLabel="Código QR Mercado Pago">
        {Platform.OS === 'web' ? (
          webGenerating || !webDataUrl ? (
            <View style={[styles.loader, { width: displaySize, height: displaySize }]}>
              <ActivityIndicator color={colors.accent} size="large" />
            </View>
          ) : (
            <img
              src={webDataUrl}
              width={displaySize}
              height={displaySize}
              alt=""
              style={{
                display: 'block',
                width: displaySize,
                height: displaySize,
                imageRendering: 'pixelated',
              }}
            />
          )
        ) : (
          <QRCode
            value={value.trim()}
            size={displaySize}
            ecl={QR_ERROR_CORRECTION}
            quietZone={QR_WHITE_PAD}
            backgroundColor="#FFFFFF"
            color="#000000"
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  host: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  whitePlate: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: QR_WHITE_PAD,
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  loader: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
